import { memo, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { Card } from '~/eds/src/components/Card/Card';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { Uuid } from '~/types/Uuid';

import { MAX_TESTCASE_NUMBER } from '../../constants';
import { useBulkExecuteModelTestCases } from '../../hooks/useBulkExecuteModelTestCases';
import { useMaintainTestCaseBuffer } from '../../hooks/useMaintainTestCaseBuffer';
import { usePollPromptModelTestCases } from '../../hooks/usePollPromptModelTestCases';
import { PromptModelTestCase } from '../../types/deprecated';
import {
  getVisibleTestCaseCount,
  mergePollTestCasesIntoCurrent,
} from '../../utils/testcase';
import { ReviewPaginate } from './ReviewPaginate';
import { ReviewSummary } from './ReviewSummary';
import { ReviewTestCase } from './ReviewTestCase';

interface Props {
  modelId: Uuid;
  testCases: PromptModelTestCase[];
  version: number;
  mode?: 'summary-only';
  readOnly?: boolean;
}

export const ReviewTestCases = memo(
  ({
    mode,
    modelId,
    readOnly,
    testCases: initialTestCases,
    version,
  }: Props) => {
    const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
    const isCustomAiAsyncTestCreationExecutionEnabled = useFlag(
      FlagType.CustomAiAsyncTestCreationExecution,
    );
    const { getSearchParam } = useRouting();
    const isTableModel =
      enableTableExtraction && getSearchParam('type') === 'table';

    const dispatch = useDispatch();
    const store = useStore();

    // Use testCases from Redux to stay in sync with activeTestCaseNumber
    const activeModel = useSelector(slices.xRay.selectors.selectActiveModel);
    const activeTableModelVersion = useSelector(
      slices.xRay.selectors.selectActiveTableModelVersion,
    );
    const testCases = isTableModel
      ? (activeTableModelVersion?.testCases ?? initialTestCases)
      : (activeModel?.testCases ?? initialTestCases);

    const activeTestCaseNumber =
      useSelector(slices.xRay.selectors.selectActiveTestcaseNumber) ??
      testCases.length;
    const furthestNavigated = useSelector(
      slices.xRay.selectors.selectFurthestNavigatedTestCaseNumber,
    );

    let navigableCount = isCustomAiAsyncTestCreationExecutionEnabled
      ? getVisibleTestCaseCount(
          testCases,
          activeTestCaseNumber,
          furthestNavigated,
        )
      : testCases.length;
    navigableCount = Math.max(
      navigableCount,
      Math.min(activeTestCaseNumber, testCases.length),
    );
    navigableCount = Math.min(navigableCount, MAX_TESTCASE_NUMBER);
    const visibleTestCases = testCases.slice(0, navigableCount);
    const activeTestCase =
      testCases[Math.min(activeTestCaseNumber, navigableCount) - 1];

    useBulkExecuteModelTestCases({
      testCases,
      modelId,
      version,
    });

    const handleUpdateTestCases = useCallback(
      (updatedTestCases: PromptModelTestCase[]) => {
        const state = store.getState() as {
          xRay?: {
            activeModel?: { testCases?: PromptModelTestCase[] };
            activeTableModelVersion?: { testCases?: PromptModelTestCase[] };
          };
        };
        const currentTestCases = isTableModel
          ? (state.xRay?.activeTableModelVersion?.testCases ?? [])
          : (state.xRay?.activeModel?.testCases ?? []);
        const testCasesToApply = mergePollTestCasesIntoCurrent(
          currentTestCases,
          updatedTestCases,
        );
        if (isTableModel) {
          dispatch(slices.xRay.actions.setTableTestCases(testCasesToApply));
        } else {
          dispatch(slices.xRay.actions.setTestCases(testCasesToApply));
        }
      },
      [store, isTableModel, dispatch],
    );

    usePollPromptModelTestCases({
      testCases,
      modelId,
      version,
      onUpdateTestCases: handleUpdateTestCases,
    });

    const onAddTestCase = useCallback(
      (testCase: PromptModelTestCase) => {
        if (isTableModel) {
          dispatch(slices.xRay.actions.addTableTestCase(testCase));
        } else {
          dispatch(slices.xRay.actions.addTestCase(testCase));
        }
      },
      [dispatch, isTableModel],
    );

    useMaintainTestCaseBuffer({
      testCases,
      modelId,
      version,
      activeTestCaseNumber,
      readOnly,
      onAddTestCase,
    });

    const handleUpdateTestcaseNumber = useCallback(
      (testcaseNumber: number) => {
        dispatch(slices.xRay.actions.setActiveTestcaseNumber(testcaseNumber));
      },
      [dispatch],
    );

    if (testCases.length === 0) {
      return null;
    }

    return (
      <Layout preset="subsections">
        <ReviewSummary
          hideNavLinks={mode === 'summary-only'}
          testCases={visibleTestCases}
          onUpdateTestCaseNumber={handleUpdateTestcaseNumber}
        />
        {mode !== 'summary-only' && (
          <Card mode="bordered">
            <ReviewTestCase testCase={activeTestCase} />
            <Layout direction="column" spacing={1}>
              <ReviewPaginate
                readOnly={readOnly}
                testCase={activeTestCase}
                totalCount={navigableCount}
                onUpdateTestCaseNumber={handleUpdateTestcaseNumber}
                testCases={testCases}
              />
            </Layout>
          </Card>
        )}
      </Layout>
    );
  },
);
